import React from 'react';
import { Form } from 'react-bootstrap';
import PicklistValueSelector from '../workflow/PicklistValueSelector';
import LookupLayout from '../layout/LookupLayout';

/**
 * DynamicFieldInput - Reusable component for rendering field inputs based on field type
 *
 * @param {Object} field - The field configuration object with data_type, field_name, etc.
 * @param {string} value - Current value (for lookup fields, this is the ID)
 * @param {string} displayValue - Display value for lookup fields
 * @param {Function} onChange - Callback when value changes (value, displayValue) for lookup
 * @param {string} operator - The operator being used (affects picklist multi-select)
 * @param {boolean} disabled - Whether the input is disabled
 * @param {string} placeholder - Placeholder text
 * @param {string} size - Bootstrap size (sm, md, lg)
 */
const DynamicFieldInput = ({
  field,
  value,
  displayValue,
  onChange,
  operator = '',
  disabled = false,
  placeholder = 'Enter value',
  size = 'sm'
}) => {
  console.log('field: ', field);
  if (!field) {
    return (
      <Form.Control
        size={size}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  }

  const fieldType = field.data_type?.toLowerCase();

  // Check if multiple values are allowed (for picklist with IN/NOT IN operators)
  const isMultipleValuesAllowed = (operator === 'IN' || operator === 'NOT IN' || operator === 'in' || operator === 'not_in');

  // Lookup/Reference fields
  if (fieldType === 'lookup' && field.is_reference === true) {
    // Parse lookup_filter if it's a string
    let lookupFilter = field.lookup_filter;
    if (lookupFilter && typeof lookupFilter === 'string') {
      try {
        lookupFilter = JSON.parse(lookupFilter);
      } catch (e) {
        console.error('Failed to parse lookup_filter:', e);
        lookupFilter = null;
      }
    }

    // Extract logic and filters from lookup_filter structure
    // Structure: { logic: "AND|OR", filters: [...] }
    let lookupFilterArray = null;
    let lookupFilterLogic = 'AND';
    console.log('DynamicFieldInput - field.lookup_filter:', field.lookup_filter);
    console.log('DynamicFieldInput - lookupFilter after parsing:', lookupFilter);
    if (lookupFilter) {
      if (typeof lookupFilter === 'object' && !Array.isArray(lookupFilter) && lookupFilter.filters) {
        lookupFilterArray = Array.isArray(lookupFilter.filters) ? lookupFilter.filters : null;
        lookupFilterLogic = lookupFilter.logic && (lookupFilter.logic.toUpperCase() === 'OR' || lookupFilter.logic.toUpperCase() === 'AND')
          ? lookupFilter.logic.toUpperCase()
          : 'AND';
        console.log('DynamicFieldInput - Extracted from object structure:', { lookupFilterArray, lookupFilterLogic });
      } else if (Array.isArray(lookupFilter)) {
        // Handle old array structure for backward compatibility
        lookupFilterArray = lookupFilter;
        lookupFilterLogic = field.lookup_filter_logic || 'AND';
        console.log('DynamicFieldInput - Extracted from array structure:', { lookupFilterArray, lookupFilterLogic });
      } else {
        console.log('DynamicFieldInput - lookupFilter structure not recognized:', typeof lookupFilter, lookupFilter);
      }
    } else {
      console.log('DynamicFieldInput - No lookupFilter found');
    }

    return (
      <LookupLayout
        objectName={field.reference_object || field.referenceObject}
        searchFields={field.lookup_search_fields || field.lookupSearchFields || 'name'}
        displayFields={field.display_fields || field.displayFields || 'name'}
        value={value || ''}
        defaultLabel={displayValue || ''}
        onChange={(selectedId, selectedLabel) => {
          // For lookup fields, we need to pass both ID and label
          if (typeof onChange === 'function') {
            onChange(selectedId, selectedLabel);
          }
        }}
        disabled={disabled}
        placeholder={placeholder || 'Search...'}
        lookupFilter={lookupFilterArray}
        lookupFilterLogic={lookupFilterLogic}
      />
    );
  }

  // Boolean fields
  if (fieldType === 'boolean' || fieldType === 'checkbox') {
    return (
      <Form.Select
        size={size}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">Select...</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </Form.Select>
    );
  }

  // Picklist fields
  if (fieldType === 'picklist' || fieldType === 'multipicklist') {
    return (
      <PicklistValueSelector
        field={field}
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
        isMultiSelect={isMultipleValuesAllowed || fieldType === 'multipicklist'}
      />
    );
  }

  // Number fields (including rollup summary)
  if (fieldType === 'number' || fieldType === 'currency' || fieldType === 'percent' || fieldType === 'integer' || fieldType === 'rollupsummary') {
    return (
      <Form.Control
        size={size}
        type="number"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        step={fieldType === 'integer' ? '1' : 'any'}
      />
    );
  }

  // Date fields
  if (fieldType === 'date') {
    return (
      <Form.Control
        size={size}
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    );
  }

  // DateTime fields
  if (fieldType === 'datetime') {
    return (
      <Form.Control
        size={size}
        type="datetime-local"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    );
  }

  // Email fields
  if (fieldType === 'email') {
    return (
      <Form.Control
        size={size}
        type="email"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  }

  // Phone fields
  if (fieldType === 'phone' || fieldType === 'telephone') {
    return (
      <Form.Control
        size={size}
        type="tel"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  }

  // URL fields
  if (fieldType === 'url') {
    return (
      <Form.Control
        size={size}
        type="url"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  }

  // Textarea for long text
  if (fieldType === 'textarea' || fieldType === 'longtext') {
    return (
      <Form.Control
        as="textarea"
        rows={2}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  }

  // Default: text input
  return (
    <Form.Control
      size={size}
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
};

export default DynamicFieldInput;
